import React, { useContext, useEffect, useMemo, useState } from 'react'
import { generatePath } from 'react-router-dom'
import { Dialog } from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  GridColumn as Column,
  Grid,
  GridCellProps,
  GridDetailRowProps,
  GridExpandChangeEvent,
} from '@progress/kendo-react-grid'
import { ClientDetailedDto } from 'api/Clients/Types/ClientDetailedDto'
import { UpdateClientStatus } from 'api/Clients/Types/UpdateClientStatus'
import { NameDto } from 'api/Common/Types'
import { PermissionsEnum } from 'api/Identity/Types/PermissionsEnum'
import { AccountGroupBasicDto } from 'api/Trading-Accounts/Types/AccountGroupDto'
import { AccountGroupType } from 'api/Trading-Accounts/Types/AccountGroupType'
import { CreateTradingAccount } from 'api/Trading-Accounts/Types/CreateTradingAccount'
import { PlatformTypeId } from 'api/Trading-Accounts/Types/PlatformTypeDto'
import { CreateWalletDto } from 'api/Wallet/Types/CreateWalletDto'
import { WalletDto } from 'api/Wallet/Types/Wallet'
import { WalletCurrencyDto } from 'api/Wallet/Types/WalletCurrencyDto'
import AddButton from 'components/AddButton/AddButton'
import { FlexBox } from 'components/Box'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { Ellipsis } from 'lucide-react'
import omitEmpty from 'omit-empty'
import { sortLeverageTypes } from 'routes/Records/TradingAccounts/Details/TradingAccountDetailsForm'
import { useAppDispatch, useAppSelector } from 'store'
import { fetchClientLeverageTypeAction } from 'store/clients/clients.actions'
import { fetchIntroducingBrokerByTickmillCompany } from 'store/partnerships/partnerships.actions'
import {
  fetchExtendedTradingAccountTypeAction,
  fetchTradingAccountClientPlatformTypeAction,
  fetchTradingAccountLeverageTypesForPlatformsAction,
} from 'store/trading-accounts/trading-accounts.actions'
import { getWalletStatesAction, getWalletTypesByClientIdAction } from 'store/wallet/wallet-actions'
import { getIdFromLocationHeader } from 'utils/api'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { PartnershipsApiClient } from 'utils/api/partnershipsApi'
import { TradingAccountsApiClient } from 'utils/api/tradingAccountsApi'
import { useCallbackWithForceRefresh } from 'utils/api/useCallbackWithForceRefresh'
import { useFetchOne } from 'utils/api/useFetch'
import { WalletsApiClient } from 'utils/api/walletsApiClient'
import { AuthSessionContext } from 'utils/AuthContext'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { hasPermission } from 'utils/permissionsValidator'

import { ClientNavigation, ClientTab } from '../../Navigation/ClientNavigation'
import clientDetailsTradingAccountWalletDetailsColumn from '../clientDetailsTradingAccountWalletDetailsColumn'
import { AddTradingAccountDetailsForm } from './AddTradingAccountForm'
import { AddWalletForm } from './AddWalletForm'
import { clientDetailsWalletColumns } from './columns'
import { ClientDetailsWalletMenuContent } from './menus'

import styles from '../ClientDetailsDocument/ClientDetailsDocument.module.scss'

const DetailComponent: React.FC<
  GridDetailRowProps & { data: ClientDetailedDto; updateTradingAccount(taId: string): void }
> = (props) => {
  const wallet: WalletDto = props.dataItem
  const data = props.data
  const hasWallet = !!wallet

  if (hasWallet) {
    return (
      <Grid data={wallet.tradingAccounts} resizable>
        {clientDetailsTradingAccountWalletDetailsColumn
          .filter((c) => c.show !== false)
          .map((column, idx) => (
            <Column key={idx + 2} {...column} />
          ))}
        <Column
          title=''
          width={50}
          locked
          minResizableWidth={50}
          cell={(gridCellProps: GridCellProps) => (
            <td className='table-columns__sticky'>
              <DropdownMenu>
                <DropdownMenuTrigger
                  render={
                    <button
                      type='button'
                      className={styles.table_menu_button}
                      aria-label='More options'
                    />
                  }
                >
                  <Ellipsis />
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  <ClientDetailsWalletMenuContent
                    wallet={props.dataItem}
                    tradingAccount={gridCellProps.dataItem}
                    updateTradingAccount={props.updateTradingAccount}
                    clientData={data}
                  />
                </DropdownMenuContent>
              </DropdownMenu>
            </td>
          )}
        />
      </Grid>
    )
  }

  return (
    <div style={{ height: '50px', width: '100%' }}>
      <div style={{ position: 'absolute', width: '100%' }}>
        <div className='k-loading-image' />
      </div>
    </div>
  )
}

interface Props {
  data: ClientDetailedDto

  forceRefresh(): void
  updateClientStatus(values: UpdateClientStatus): void
}

export const ClientDetailsWallet: React.FC<Props> = ({ data, updateClientStatus }) => {
  const walletsApiClient = useApiClient(WalletsApiClient)
  const clientsApiClient = useApiClient(ClientsApiClient)
  const tradingAccountsApiClient = useApiClient(TradingAccountsApiClient)
  const partnershipsApiClient = useApiClient(PartnershipsApiClient)

  const [auth] = useContext(AuthSessionContext)
  const permissions = auth?.permissions || []
  const [selectedWallet, setSelectedWallet] = useState<WalletDto>()
  const [wallets, setWallets] = useState<WalletDto[]>([])
  const [platformTypes, setPlatformTypes] = useState<NameDto[]>()
  const [availableWalletCurrenciesLoading, setAvailableWalletCurrenciesLoading] = useState(false)
  const [availableWalletCurrencies, setAvailableWalletCurrencies] = useState<WalletCurrencyDto[]>(
    []
  )
  const [accountGroups, setAccountGroups] = useState<AccountGroupBasicDto[]>([])

  const tradingAccountTypes = useAppSelector(
    (state) => state.tradingAccounts.extendedTradingAccountType
  )
  const introducingBrokers = useAppSelector(
    (state) => state.partnerships.introducingBrokersByCompany[data.tickmillCompany.id] || []
  )
  const leverageType = useAppSelector((state) => state.tradingAccounts.leverageForPlatforms)
  const walletStates = useAppSelector((state) => state.wallet.states)
  const walletTypes = useAppSelector((state) => state.wallet.clientWalletTypes)

  const [isAddTradingAccountModalOpen, setAddTradingAccountModalOpen] = useState<boolean>(false)
  const setToast = useContext(ToastContext)

  const sortedLeverageTypes = useMemo(
    () => [...leverageType].sort(sortLeverageTypes),
    [leverageType]
  )

  const [isAddWalletModalOpen, setAddWalletModalOpen] = useState<boolean>(false)
  const dispatch = useAppDispatch()
  const updatePlatformTypes = async (wallet: WalletDto) => {
    if (!wallet.walletType.tickmillProduct) return []
    const types = await tradingAccountsApiClient.getTradingAccountClientPlatformType(
      data.id,
      wallet.walletType.tickmillProduct?.id
    )
    if (wallet?.currency.id === 'USD') return types
    return types.filter((platform) => platform.id !== PlatformTypeId.TickmillTrader)
  }

  useEffect(() => {
    dispatch(
      fetchIntroducingBrokerByTickmillCompany(partnershipsApiClient, data.tickmillCompany.id)
    )
    if (isEmpty(walletStates)) dispatch(getWalletStatesAction(walletsApiClient))
    dispatch(getWalletTypesByClientIdAction(data.id, walletsApiClient))
  }, [data.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const { callback, forceRefresh: forceRefreshWallets } = useCallbackWithForceRefresh(
    () => tradingAccountsApiClient.getWalletWithTradingAccountByClientId(data.id),
    [data.id]
  )
  const { data: walletsData, isLoading } = useFetchOne(callback)

  useEffect(() => {
    if (walletsData) setWallets(walletsData.map((x) => ({ ...x, expanded: true })))
  }, [walletsData])

  const getAccountTypeOptions = async ({
    platformTypeId,
    currencyId,
    accountGroupTypeId,
  }: {
    platformTypeId: number
    currencyId: string
    accountGroupTypeId: number
  }) => {
    dispatch(
      fetchExtendedTradingAccountTypeAction(tradingAccountsApiClient, {
        platformTypeId,
        currencyId,
        accountGroupTypeId,
      })
    )
  }

  const getLeverageOptions = async ({ platformTypeId }: { platformTypeId: number }) => {
    dispatch(
      fetchTradingAccountLeverageTypesForPlatformsAction(
        data.id,
        tradingAccountsApiClient,
        platformTypeId
      )
    )
  }

  const getAccountGroupsOptions = async ({
    platformTypeId,
    tradingAccountTypeId,
    introducingBrokerId,
  }: {
    platformTypeId: string
    tradingAccountTypeId: string
    introducingBrokerId?: string
  }) => {
    const accountGroupsList =
      await tradingAccountsApiClient.getAccountGroupByWalletPlatformTradingAccountTypeAndIntroducingBrokerId(
        omitEmpty({
          walletId: selectedWallet?.id,
          platformTypeId,
          tradingAccountTypeId,
          introducingBrokerId,
        })
      )
    setAccountGroups(accountGroupsList)
  }

  ;[]

  const handleAddTradingAccount = async (selectedWalletId: string) => {
    const selectedCurrentWallet = wallets.find((x) => x.id === selectedWalletId)
    if (selectedCurrentWallet) {
      setSelectedWallet(selectedCurrentWallet)
      const pTypes = await updatePlatformTypes(selectedCurrentWallet)

      setPlatformTypes(pTypes)
      await handleChangePlatform(pTypes?.[0].id as number, selectedCurrentWallet.currency.id)
      if (isEmpty(tradingAccountTypes)) {
        dispatch(
          fetchExtendedTradingAccountTypeAction(tradingAccountsApiClient, {
            platformTypeId: PlatformTypeId.MT4, // default one
            currencyId: selectedCurrentWallet?.currency.id,
            accountGroupTypeId: AccountGroupType.Live,
          })
        )
      }
      setAddTradingAccountModalOpen(true)
    }
  }

  const handleUpdateTradingAccount = async (taId: string) => {
    const wallet = wallets.find((x) => x.tradingAccounts.find((ta) => ta.id === taId))
    if (!wallet) return
    const tradingAccount = wallet.tradingAccounts.find((ta) => ta.id === taId)
    if (!tradingAccount) return
    const campaign = tradingAccount.campaigns?.[0]
    if (!campaign) return
    await tradingAccountsApiClient.updateTradingAccountAutomaticClosure(taId, {
      automaticClosure: !campaign.automaticClosure,
    })
    setToast(successToast('Automatic Closure for Trading Account has been updated.'))
    forceRefreshWallets()
  }

  useEffect(() => {
    if (selectedWallet?.walletType.tickmillProduct) {
      dispatch(
        fetchTradingAccountClientPlatformTypeAction(
          tradingAccountsApiClient,
          data.id,
          selectedWallet.walletType.tickmillProduct.id
        )
      )
      dispatch(
        fetchClientLeverageTypeAction(data.id, clientsApiClient, {
          tickmillProductId: selectedWallet.walletType.tickmillProduct.id,
        })
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWallet])

  const getAvailableWalletCurrencies = async (walletTypeId: number) => {
    try {
      setAvailableWalletCurrenciesLoading(true)
      const currencies = await walletsApiClient.getAvailableCurrenciesForClient({
        clientId: data.id,
        walletTypeId,
      })
      setAvailableWalletCurrencies(currencies)
    } finally {
      setAvailableWalletCurrenciesLoading(false)
    }
  }

  const handleExpand = (event: GridExpandChangeEvent) => {
    setWallets(
      wallets.map((walletItem) =>
        walletItem.id === event.dataItem.id ? { ...walletItem, expanded: event.value } : walletItem
      )
    )
  }

  const onAddWalletSubmit = async (values: CreateWalletDto) => {
    const response = await walletsApiClient.createWallet(values)
    const id = getIdFromLocationHeader(response.headers.location)
    setToast({
      type: 'success',
      title: `Wallet with currency ${values.currencyId} created.`,
      subtitle: 'Click for details',
      url: generatePath(links.recordsWallets.detailsUrl as string, {
        id,
        slug: 'details',
      }),
    })
    setAddWalletModalOpen(false)
    forceRefreshWallets()
  }

  const addTradingAccount = async (values: CreateTradingAccount) => {
    await tradingAccountsApiClient.createTradingAccount(values)
    setToast(successToast('Trading Account created.'))
    setAddTradingAccountModalOpen(false)
    forceRefreshWallets()
  }

  const handleChangePlatform = async (platformTypeId: number, currencyId?: string) => {
    if (currencyId || selectedWallet?.currency.id) {
      getAccountTypeOptions({
        platformTypeId,
        currencyId: currencyId ?? selectedWallet?.currency.id ?? '',
        accountGroupTypeId: AccountGroupType.Live,
      })
    }
    getLeverageOptions({ platformTypeId })
  }

  if (isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      <ClientNavigation
        isEdit={false}
        data={data}
        tab={ClientTab.WALLETS}
        updateClientStatus={updateClientStatus}
      />
      <FlexBox alignItems='center' flexDirection='column' justifyContent='flex-start'>
        {isAddWalletModalOpen && (
          <Dialog open={isAddWalletModalOpen} onOpenChange={setAddWalletModalOpen}>
            <AddWalletForm
              clientId={data.id}
              walletStates={walletStates}
              onClose={() => setAddWalletModalOpen(false)}
              availableWalletCurrencies={availableWalletCurrencies}
              availableWalletCurrenciesLoading={availableWalletCurrenciesLoading}
              walletTypes={walletTypes}
              onSubmit={onAddWalletSubmit}
              getAvailableWalletCurrencies={getAvailableWalletCurrencies}
            />
          </Dialog>
        )}
        {isAddTradingAccountModalOpen && selectedWallet && (
          <Dialog open={isAddTradingAccountModalOpen} onOpenChange={setAddTradingAccountModalOpen}>
            <AddTradingAccountDetailsForm
              onChangePlatformType={handleChangePlatform}
              client={data}
              wallet={selectedWallet}
              permissions={permissions}
              tradingAccountTypes={tradingAccountTypes}
              introducingBrokers={introducingBrokers}
              leverageType={sortedLeverageTypes}
              platformTypes={platformTypes}
              onSubmit={addTradingAccount}
              getAccountGroupsOptions={getAccountGroupsOptions}
              accountGroups={accountGroups}
            />
          </Dialog>
        )}
        <AddButton
          text='Add wallet'
          disabled={!hasPermission(permissions, PermissionsEnum.CanAddWallet)}
          onClick={() => setAddWalletModalOpen(true)}
        />
        <Grid
          resizable
          data={wallets}
          detail={(gridDetailProps) => (
            <DetailComponent
              updateTradingAccount={handleUpdateTradingAccount}
              dataIndex={gridDetailProps.dataIndex}
              dataItem={gridDetailProps.dataItem}
              data={data}
            />
          )}
          expandField='expanded'
          onExpandChange={handleExpand}
        >
          {clientDetailsWalletColumns.map((column, idx) => (
            <Column key={idx} {...column} />
          ))}
          <Column
            width={50}
            title=''
            cell={(props: GridCellProps) => (
              <td className='table-columns__sticky'>
                <DropdownMenu>
                  <DropdownMenuTrigger
                    render={
                      <button
                        type='button'
                        className={styles.table_menu_button}
                        aria-label='More options'
                      />
                    }
                  >
                    <Ellipsis />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align='end'>
                    <ClientDetailsWalletMenuContent
                      wallet={props.dataItem}
                      handleAddTradingAccount={handleAddTradingAccount}
                      clientData={data}
                    />
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            )}
          />
        </Grid>
      </FlexBox>
    </React.Fragment>
  )
}
